import React, { useState, useEffect } from 'react'
import { bool, node } from 'prop-types'
import { useIntl } from 'react-intl'
import styled, { useTheme } from 'styled-components'

import { useFormContext } from '../../Form'
import { Button } from '../../Button'
import { Input, TextInput } from '../../Input/'
import { authenticationMessages, validationMessages } from '../../../translations'
import { passwordValidations, required } from '../../..//utils/validations'
import { CloseCircle, CircleCheck } from '../../../icons'

const PasswordWrapper = styled.div`
  position: relative;
`
const SubLinks = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;

  a {
    font-size: 0.7em;
    color: #1b77da;
    text-decoration: underline;
  }
`

const ValidationWrapper = styled.div`
  font-size: 0.875rem;
  margin-bottom: 1rem;
`

const ValidReq = styled.p`
  margin: 0;
  line-height: 1rem;
  color: ${({ theme }) => theme.donate};
  display: flex;
  align-items: center;

  svg {
    margin-right: 5px;
  }
`

const InvalidReq = styled.p`
  margin: 0;
  line-height: 1rem;
  color: ${({ theme }) => theme.danger};
  display: flex;
  align-items: center;

  svg {
    margin-right: 5px;
  }
`

const Requirement = ({ valid, children }) => {
  const theme = useTheme()
  if (valid) {
    return (
      <ValidReq>
        <CircleCheck stroke={theme.donate} fill={theme.donate} height={14} width={14} /> {children}
      </ValidReq>
    )
  }

  return (
    <InvalidReq>
      <CloseCircle stroke={theme.danger} fill={theme.danger} height={14} width={14} /> {children}
    </InvalidReq>
  )
}

export const PasswordInput = ({ showForget, showValidations }) => {
  const { formatMessage } = useIntl()
  const { values } = useFormContext()
  const [requirementsMet, setRequirementsMet] = useState({
    lowerCaseLetters: false,
    upperCaseLetters: false,
    numbers: false,
    specialChar: false,
    length: false,
  })

  useEffect(() => {
    testPassword(values.password)
  }, [])

  const testPassword = (value) => {
    const lowerCaseLetters = value?.length > 0 && /[a-z]/g.test(value)
    const upperCaseLetters = /[A-Z]/g.test(value)
    const numbers = /[0-9]/g.test(value)
    const specialChar = /(?=.*[!@#$%^&*])/g.test(value)
    const length = value?.length >= 8

    setRequirementsMet({
      lowerCaseLetters,
      upperCaseLetters,
      numbers,
      specialChar,
      length,
    })
  }

  const validate = (value) => {
    testPassword(value)
    if (!showValidations) return required(value, formatMessage)

    return passwordValidations(value, formatMessage)
  }

  return (
    <PasswordWrapper>
      <Input
        type='password'
        component={TextInput}
        name='password'
        label={formatMessage(authenticationMessages.passwordLabel)}
        validate={validate}
        required
      />
      {showForget && (
        <SubLinks>
          <Button as='a' href='/reset-password'>
            {formatMessage(authenticationMessages.forgot)}
          </Button>
        </SubLinks>
      )}
      {showValidations && (
        <ValidationWrapper>
          <Requirement valid={requirementsMet.lowerCaseLetters}>
            {formatMessage(validationMessages.lowerCase)}
          </Requirement>
          <Requirement valid={requirementsMet.upperCaseLetters}>
            {formatMessage(validationMessages.upperCase)}
          </Requirement>
          <Requirement valid={requirementsMet.numbers}>
            {formatMessage(validationMessages.number)}
          </Requirement>
          <Requirement valid={requirementsMet.specialChar}>
            {formatMessage(validationMessages.symbol)}
          </Requirement>
          <Requirement valid={requirementsMet.length}>
            {formatMessage(validationMessages.minLength, { min: 8 })}
          </Requirement>
        </ValidationWrapper>
      )}
    </PasswordWrapper>
  )
}

Requirement.propTypes = {
  valid: bool,
  children: node,
}

PasswordInput.propTypes = {
  showForget: bool,
  showValidations: bool,
}
